import React, { useCallback, useState } from 'react';
import { Button, OutlinedInput, InputLabel, FormControl, Checkbox, FormControlLabel, Box, Card, CardContent, Typography, Divider, InputAdornment } from '@mui/material';
import ScheduleIcon from '@mui/icons-material/Schedule';
import RssFeedIcon from '@mui/icons-material/RssFeed';
import TimelineIcon from '@mui/icons-material/Timeline';
import SearchIcon from '@mui/icons-material/Search';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import RepeatIcon from '@mui/icons-material/Repeat';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { formStyle } from '../styles';
import ConfirmDialog from '../components/ConfirmDialog';

const BotsConfigEditForm = ({ handleSubmit, handleCancel, botsConfig, handleChange }) => {
    const [changed, setChanged] = useState(false);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

    const handleChangeInternal = useCallback(newValue => {
        handleChange(newValue);
        setChanged(true);
    }, [handleChange]);

    return (
        <Box sx={{ formStyle }}>
            {/* Schedule Section */}
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                        <ScheduleIcon sx={{ mr: 1 }} />
                        <Typography variant="h6">Schedule</Typography>
                    </Box>
                    <Box display="flex" flexWrap="wrap" gap={2}>
                        <FormControl sx={{ minWidth: 200, flex: 1, maxWidth: 300 }} variant="outlined">
                            <InputLabel htmlFor="min-delay-hours">Min Delay Hours</InputLabel>
                            <OutlinedInput
                                type='number'
                                id='min-delay-hours'
                                value={botsConfig?.schedule.minDelayHours ?? ''}
                                onChange={(e) => handleChangeInternal({ ...botsConfig, schedule: { ...botsConfig.schedule, minDelayHours: parseInt(e.target.value, 10) || 0 } })}
                                label="Min Delay Hours"
                            />
                        </FormControl>
                        <FormControl sx={{ minWidth: 200, flex: 1, maxWidth: 300 }} variant="outlined">
                            <InputLabel htmlFor="max-delay-hours">Max Delay Hours</InputLabel>
                            <OutlinedInput
                                type='number'
                                id='max-delay-hours'
                                value={botsConfig?.schedule.maxDelayHours ?? ''}
                                onChange={(e) => handleChangeInternal({ ...botsConfig, schedule: { ...botsConfig.schedule, maxDelayHours: parseInt(e.target.value, 10) || 0 } })}
                                label="Max Delay Hours"
                            />
                        </FormControl>
                    </Box>
                </CardContent>
            </Card>

            {/* Run Limits Section */}
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                        <Typography variant="h6">Run Limits</Typography>
                    </Box>
                    <Box display="flex" flexWrap="wrap" gap={2}>
                        <FormControl sx={{ minWidth: 200, flex: 1, maxWidth: 300 }} variant="outlined">
                            <InputLabel htmlFor="max-likes-per-run">Max Likes Per Run</InputLabel>
                            <OutlinedInput
                                type='number'
                                id='max-likes-per-run'
                                value={botsConfig?.maxLikesPerRun ?? ''}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (value === '') {
                                        handleChangeInternal({ ...botsConfig, maxLikesPerRun: undefined });
                                    } else {
                                        const numValue = parseInt(value, 10);
                                        handleChangeInternal({ ...botsConfig, maxLikesPerRun: isNaN(numValue) ? undefined : numValue });
                                    }
                                }}
                                label="Max Likes Per Run"
                                placeholder="No limit"
                            />
                        </FormControl>
                        <FormControl sx={{ minWidth: 200, flex: 1, maxWidth: 300 }} variant="outlined">
                            <InputLabel htmlFor="max-follows-per-run">Max Follows Per Run</InputLabel>
                            <OutlinedInput
                                type='number'
                                id='max-follows-per-run'
                                value={botsConfig?.maxFollowsPerRun ?? 100}
                                onChange={(e) => handleChangeInternal({ ...botsConfig, maxFollowsPerRun: parseInt(e.target.value, 10) || 100 })}
                                label="Max Follows Per Run"
                                required
                            />
                        </FormControl>
                        <FormControl sx={{ minWidth: 200, flex: 1, maxWidth: 300 }} variant="outlined">
                            <InputLabel htmlFor="max-reposts-per-run">Max Reposts Per Run</InputLabel>
                            <OutlinedInput
                                type='number'
                                id='max-reposts-per-run'
                                value={botsConfig?.maxRepostsPerRun ?? ''}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (value === '') {
                                        handleChangeInternal({ ...botsConfig, maxRepostsPerRun: undefined });
                                    } else {
                                        const numValue = parseInt(value, 10);
                                        handleChangeInternal({ ...botsConfig, maxRepostsPerRun: isNaN(numValue) ? undefined : numValue });
                                    }
                                }}
                                label="Max Reposts Per Run"
                                placeholder="No limit"
                            />
                        </FormControl>
                    </Box>
                </CardContent>
            </Card>

            {/* Quality Filter Section */}
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                        <FilterAltIcon sx={{ mr: 1 }} />
                        <Typography variant="h6">Quality Filters</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', textAlign: 'start' }}>
                        Filter out low-quality accounts (spam, bots, inactive users) to improve bot effectiveness
                    </Typography>
                    <Box display="flex" justifyContent="flex-start" sx={{ mb: 2 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={botsConfig?.qualityFilter?.enabled ?? false}
                                    onChange={(e) => handleChangeInternal({ 
                                        ...botsConfig, 
                                        qualityFilter: { 
                                            ...(botsConfig?.qualityFilter || {}), 
                                            enabled: e.target.checked 
                                        } 
                                    })}
                                />
                            }
                            label="Enable Quality Filters"
                        />
                    </Box>
                    {botsConfig?.qualityFilter?.enabled && (
                        <Box display="flex" flexWrap="wrap" gap={2}>
                            <FormControl sx={{ minWidth: 200, flex: 1, maxWidth: 300 }} variant="outlined">
                                <InputLabel htmlFor="min-followers">Min Followers</InputLabel>
                                <OutlinedInput
                                    type='number'
                                    id='min-followers'
                                    value={botsConfig?.qualityFilter?.minFollowers ?? 10}
                                    onChange={(e) => handleChangeInternal({ 
                                        ...botsConfig, 
                                        qualityFilter: { 
                                            ...(botsConfig?.qualityFilter || {}), 
                                            minFollowers: parseInt(e.target.value, 10) || 10 
                                        } 
                                    })}
                                    label="Min Followers"
                                />
                            </FormControl>
                            <FormControl sx={{ minWidth: 200, flex: 1, maxWidth: 300 }} variant="outlined">
                                <InputLabel htmlFor="max-following-ratio">Max Following/Follower Ratio</InputLabel>
                                <OutlinedInput
                                    type='number'
                                    id='max-following-ratio'
                                    value={botsConfig?.qualityFilter?.maxFollowingToFollowerRatio ?? 10}
                                    onChange={(e) => handleChangeInternal({ 
                                        ...botsConfig, 
                                        qualityFilter: { 
                                            ...(botsConfig?.qualityFilter || {}), 
                                            maxFollowingToFollowerRatio: parseInt(e.target.value, 10) || 10 
                                        } 
                                    })}
                                    label="Max Following/Follower Ratio"
                                />
                            </FormControl>
                            <FormControl sx={{ minWidth: 200, flex: 1, maxWidth: 300 }} variant="outlined">
                                <InputLabel htmlFor="min-posts">Min Posts</InputLabel>
                                <OutlinedInput
                                    type='number'
                                    id='min-posts'
                                    value={botsConfig?.qualityFilter?.minPosts ?? 5}
                                    onChange={(e) => handleChangeInternal({ 
                                        ...botsConfig, 
                                        qualityFilter: { 
                                            ...(botsConfig?.qualityFilter || {}), 
                                            minPosts: parseInt(e.target.value, 10) || 5 
                                        } 
                                    })}
                                    label="Min Posts"
                                />
                            </FormControl>
                            <FormControl sx={{ minWidth: 200, flex: 1, maxWidth: 300 }} variant="outlined">
                                <InputLabel htmlFor="min-account-age">Min Account Age (days)</InputLabel>
                                <OutlinedInput
                                    type='number'
                                    id='min-account-age'
                                    value={botsConfig?.qualityFilter?.minAccountAgeDays ?? 7}
                                    onChange={(e) => handleChangeInternal({ 
                                        ...botsConfig, 
                                        qualityFilter: { 
                                            ...(botsConfig?.qualityFilter || {}), 
                                            minAccountAgeDays: parseInt(e.target.value, 10) || 7 
                                        } 
                                    })}
                                    label="Min Account Age (days)"
                                />
                            </FormControl>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Feeds Bot Section */}
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                        <RssFeedIcon sx={{ mr: 1 }} />
                        <Typography variant="h6">Feeds Bot</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                        <FormControlLabel 
                            control={<Checkbox checked={botsConfig?.feedsBot?.enabled} onChange={(e) => handleChangeInternal({ ...botsConfig, feedsBot: { ...botsConfig.feedsBot, enabled: e.target.checked } })} />} 
                            label="Enabled" 
                        />
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Box display="flex" flexWrap="wrap" gap={2}>
                        <FormControl sx={{ minWidth: 200, flex: 1, maxWidth: 300 }} variant="outlined">
                            <InputLabel htmlFor="feed-like-perc">Like Percentage</InputLabel>
                            <OutlinedInput
                                type='number'
                                id='feed-like-perc'
                                value={botsConfig?.feedsBot.likePercentage ?? ''}
                                onChange={(e) => handleChangeInternal({ ...botsConfig, feedsBot: { ...botsConfig.feedsBot, likePercentage: parseInt(e.target.value, 10) || 0 } })}
                                label="Like Percentage"
                                endAdornment={<InputAdornment position="end">%</InputAdornment>}
                            />
                        </FormControl>
                        <FormControl sx={{ minWidth: 200, flex: 1, maxWidth: 300 }} variant="outlined">
                            <InputLabel htmlFor="feed-like-limit">Like Limit</InputLabel>
                            <OutlinedInput
                                type='number'
                                id='feed-like-limit'
                                value={botsConfig?.feedsBot.limit ?? ''}
                                onChange={(e) => handleChangeInternal({ ...botsConfig, feedsBot: { ...botsConfig.feedsBot, limit: parseInt(e.target.value, 10) || 0 } })}
                                label="Like Limit"
                            />
                        </FormControl>
                        <FormControl sx={{ minWidth: 200, flex: 1, maxWidth: 300 }} variant="outlined">
                            <InputLabel htmlFor="feed-follow-perc">Follow Percentage</InputLabel>
                            <OutlinedInput
                                type='number'
                                id='feed-follow-perc'
                                value={botsConfig?.feedsBot.followPercentage ?? ''}
                                onChange={(e) => handleChangeInternal({ ...botsConfig, feedsBot: { ...botsConfig.feedsBot, followPercentage: parseInt(e.target.value, 10) || 0 } })}
                                label="Follow Percentage"
                                endAdornment={<InputAdornment position="end">%</InputAdornment>}
                            />
                        </FormControl>
                        <FormControl sx={{ minWidth: 200, flex: 1, maxWidth: 300 }} variant="outlined">
                            <InputLabel htmlFor="feed-repost-perc">Repost Percentage</InputLabel>
                            <OutlinedInput
                                type='number'
                                id='feed-repost-perc'
                                value={botsConfig?.feedsBot.repostPercentage ?? ''}
                                onChange={(e) => handleChangeInternal({ ...botsConfig, feedsBot: { ...botsConfig.feedsBot, repostPercentage: parseInt(e.target.value, 10) || 0 } })}
                                label="Repost Percentage"
                                endAdornment={<InputAdornment position="end">%</InputAdornment>}
                            />
                        </FormControl>
                    </Box>
                </CardContent>
            </Card>

            {/* Timeline Bot Section */}
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                        <TimelineIcon sx={{ mr: 1 }} />
                        <Typography variant="h6">Timeline Bot</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                        <FormControlLabel 
                            control={<Checkbox checked={botsConfig?.timelineBot?.enabled} onChange={(e) => handleChangeInternal({ ...botsConfig, timelineBot: { ...botsConfig.timelineBot, enabled: e.target.checked } })} />} 
                            label="Enabled" 
                        />
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Box display="flex" flexWrap="wrap" gap={2}>
                        <FormControl sx={{ minWidth: 200, flex: 1, maxWidth: 300 }} variant="outlined">
                            <InputLabel htmlFor="timeline-like-perc">Like Percentage</InputLabel>
                            <OutlinedInput
                                type='number'
                                id='timeline-like-perc'
                                value={botsConfig?.timelineBot.likePercentage ?? ''}
                                onChange={(e) => handleChangeInternal({ ...botsConfig, timelineBot: { ...botsConfig.timelineBot, likePercentage: parseInt(e.target.value, 10) || 0 } })}
                                label="Like Percentage"
                                endAdornment={<InputAdornment position="end">%</InputAdornment>}
                            />
                        </FormControl>
                        <FormControl sx={{ minWidth: 200, flex: 1, maxWidth: 300 }} variant="outlined">
                            <InputLabel htmlFor="timeline-like-limit">Like Limit</InputLabel>
                            <OutlinedInput
                                type='number'
                                id='timeline-like-limit'
                                value={botsConfig?.timelineBot.limit ?? ''}
                                onChange={(e) => handleChangeInternal({ ...botsConfig, timelineBot: { ...botsConfig.timelineBot, limit: parseInt(e.target.value, 10) || 0 } })}
                                label="Like Limit"
                            />
                        </FormControl>
                        <FormControl sx={{ minWidth: 200, flex: 1, maxWidth: 300 }} variant="outlined">
                            <InputLabel htmlFor="timeline-repost-perc">Repost Percentage</InputLabel>
                            <OutlinedInput
                                type='number'
                                id='timeline-repost-perc'
                                value={botsConfig?.timelineBot.repostPercentage ?? ''}
                                onChange={(e) => handleChangeInternal({ ...botsConfig, timelineBot: { ...botsConfig.timelineBot, repostPercentage: parseInt(e.target.value, 10) || 0 } })}
                                label="Repost Percentage"
                                endAdornment={<InputAdornment position="end">%</InputAdornment>}
                            />
                        </FormControl>
                    </Box>
                </CardContent>
            </Card>

            {/* Search Bot Section */}
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                        <SearchIcon sx={{ mr: 1 }} />
                        <Typography variant="h6">Search Bot</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                        <FormControlLabel 
                            control={<Checkbox checked={botsConfig?.searchBot?.enabled} onChange={(e) => handleChangeInternal({ ...botsConfig, searchBot: { ...botsConfig.searchBot, enabled: e.target.checked } })} />} 
                            label="Enabled" 
                        />
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Box display="flex" flexDirection="column" gap={2}>
                        <FormControl variant="outlined" fullWidth>
                            <InputLabel htmlFor="queries-like-perc">Queries (comma separated)</InputLabel>
                            <OutlinedInput
                                type='text'
                                id='queries-like-perc'
                                value={botsConfig?.searchBot.queries}
                                onChange={(e) => handleChangeInternal({ ...botsConfig, searchBot: { ...botsConfig.searchBot, queries: e.target.value } })}
                                label="Queries (comma separated)"
                            />
                        </FormControl>
                        <Box display="flex" flexWrap="wrap" gap={2}>
                            <FormControl sx={{ minWidth: 200, flex: 1, maxWidth: 300 }} variant="outlined">
                                <InputLabel htmlFor="search-like-perc">Like Percentage</InputLabel>
                                <OutlinedInput
                                    type='number'
                                    id='search-like-perc'
                                    value={botsConfig?.searchBot.likePercentage ?? ''}
                                    onChange={(e) => handleChangeInternal({ ...botsConfig, searchBot: { ...botsConfig.searchBot, likePercentage: parseInt(e.target.value, 10) || 0 } })}
                                    label="Like Percentage"
                                    endAdornment={<InputAdornment position="end">%</InputAdornment>}
                                />
                            </FormControl>
                            <FormControl sx={{ minWidth: 200, flex: 1, maxWidth: 300 }} variant="outlined">
                                <InputLabel htmlFor="search-like-limit">Like Limit</InputLabel>
                                <OutlinedInput
                                    type='number'
                                    id='search-like-limit'
                                    value={botsConfig?.searchBot.limit ?? ''}
                                    onChange={(e) => handleChangeInternal({ ...botsConfig, searchBot: { ...botsConfig.searchBot, limit: parseInt(e.target.value, 10) || 0 } })}
                                    label="Like Limit"
                                />
                            </FormControl>
                            <FormControl sx={{ minWidth: 200, flex: 1, maxWidth: 300 }} variant="outlined">
                                <InputLabel htmlFor="search-follow-perc">Follow Percentage</InputLabel>
                                <OutlinedInput
                                    type='number'
                                    id='search-follow-perc'
                                    value={botsConfig?.searchBot.followPercentage ?? ''}
                                    onChange={(e) => handleChangeInternal({ ...botsConfig, searchBot: { ...botsConfig.searchBot, followPercentage: parseInt(e.target.value, 10) || 0 } })}
                                    label="Follow Percentage"
                                    endAdornment={<InputAdornment position="end">%</InputAdornment>}
                                />
                            </FormControl>
                            <FormControl sx={{ minWidth: 200, flex: 1, maxWidth: 300 }} variant="outlined">
                                <InputLabel htmlFor="search-repost-perc">Repost Percentage</InputLabel>
                                <OutlinedInput
                                    type='number'
                                    id='search-repost-perc'
                                    value={botsConfig?.searchBot.repostPercentage ?? ''}
                                    onChange={(e) => handleChangeInternal({ ...botsConfig, searchBot: { ...botsConfig.searchBot, repostPercentage: parseInt(e.target.value, 10) || 0 } })}
                                    label="Repost Percentage"
                                    endAdornment={<InputAdornment position="end">%</InputAdornment>}
                                />
                            </FormControl>
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            {/* Unfollow Bot Section */}
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                        <PersonRemoveIcon sx={{ mr: 1 }} />
                        <Typography variant="h6">Unfollow Bot</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                        <FormControlLabel 
                            control={<Checkbox checked={botsConfig?.unfollowBot?.enabled} onChange={(e) => handleChangeInternal({ ...botsConfig, unfollowBot: { ...botsConfig.unfollowBot, enabled: e.target.checked } })} />} 
                            label="Enabled" 
                        />
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Box display="flex" flexWrap="wrap" gap={2}>
                        <FormControl sx={{ minWidth: 200, flex: 1, maxWidth: 300 }} variant="outlined">
                            <InputLabel htmlFor="unfollow-bot-min">Min Users to unfollow</InputLabel>
                            <OutlinedInput
                                type='number'
                                id='unfollow-bot-min'
                                value={botsConfig?.unfollowBot.unfollowCountMin ?? ''}
                                onChange={(e) => handleChangeInternal({ ...botsConfig, unfollowBot: { ...botsConfig.unfollowBot, unfollowCountMin: parseInt(e.target.value, 10) || 0 } })}
                                label="Min Users to unfollow"
                            />
                        </FormControl>
                        <FormControl sx={{ minWidth: 200, flex: 1, maxWidth: 300 }} variant="outlined">
                            <InputLabel htmlFor="unfollow-bot-max">Max Users to unfollow</InputLabel>
                            <OutlinedInput
                                type='number'
                                id='unfollow-bot-max'
                                value={botsConfig?.unfollowBot.unfollowCountMax ?? ''}
                                onChange={(e) => handleChangeInternal({ ...botsConfig, unfollowBot: { ...botsConfig.unfollowBot, unfollowCountMax: parseInt(e.target.value, 10) || 0 } })}
                                label="Max Users to unfollow"
                            />
                        </FormControl>
                        <FormControl sx={{ minWidth: 200, flex: 1, maxWidth: 300 }} variant="outlined">
                            <InputLabel htmlFor="unfollow-after-days">Unfollow After Days</InputLabel>
                            <OutlinedInput
                                type='number'
                                id='unfollow-after-days'
                                value={botsConfig?.unfollowBot.unfollowAfterDays ?? ''}
                                onChange={(e) => handleChangeInternal({ ...botsConfig, unfollowBot: { ...botsConfig.unfollowBot, unfollowAfterDays: parseInt(e.target.value, 10) || 0 } })}
                                label="Unfollow After Days"
                                endAdornment={<InputAdornment position="end">days</InputAdornment>}
                            />
                        </FormControl>
                    </Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', mt: 2, display: 'block', textAlign: 'start' }}>
                        Note: Accounts that follow you will automatically be kept (not unfollowed)
                    </Typography>
                </CardContent>
            </Card>

            {/* Reposter Bot Section */}
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                        <RepeatIcon sx={{ mr: 1 }} />
                        <Typography variant="h6">Reposter Bot</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                        <FormControlLabel 
                            control={<Checkbox checked={botsConfig?.reposterBot?.enabled ?? false} onChange={(e) => handleChangeInternal({ ...botsConfig, reposterBot: { ...(botsConfig?.reposterBot || {}), enabled: e.target.checked } })} />} 
                            label="Enabled" 
                        />
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Box display="flex" flexDirection="column" gap={2}>
                        <FormControl variant="outlined" fullWidth>
                            <InputLabel htmlFor="reposter-accounts">Accounts to Repost From (comma separated)</InputLabel>
                            <OutlinedInput
                                type='text'
                                id='reposter-accounts'
                                value={botsConfig?.reposterBot?.accounts ?? ''}
                                onChange={(e) => handleChangeInternal({ ...botsConfig, reposterBot: { ...(botsConfig?.reposterBot || {}), accounts: e.target.value } })}
                                label="Accounts to Repost From (comma separated)"
                                placeholder="handle1.bsky.social, handle2.bsky.social"
                            />
                        </FormControl>
                        <Box display="flex" flexWrap="wrap" gap={2}>
                            <FormControl sx={{ minWidth: 200, flex: 1, maxWidth: 300 }} variant="outlined">
                                <InputLabel htmlFor="reposter-posts-per-account">Posts Per Account</InputLabel>
                                <OutlinedInput
                                    type='number'
                                    id='reposter-posts-per-account'
                                    value={botsConfig?.reposterBot?.postsPerAccount ?? ''}
                                    onChange={(e) => handleChangeInternal({ ...botsConfig, reposterBot: { ...(botsConfig?.reposterBot || {}), postsPerAccount: parseInt(e.target.value, 10) || 5 } })}
                                    label="Posts Per Account"
                                />
                            </FormControl>
                            <FormControl sx={{ minWidth: 200, flex: 1, maxWidth: 300 }} variant="outlined">
                                <InputLabel htmlFor="reposter-percentage">Repost Percentage</InputLabel>
                                <OutlinedInput
                                    type='number'
                                    id='reposter-percentage'
                                    value={botsConfig?.reposterBot?.repostPercentage ?? ''}
                                    onChange={(e) => handleChangeInternal({ ...botsConfig, reposterBot: { ...(botsConfig?.reposterBot || {}), repostPercentage: parseInt(e.target.value, 10) || 100 } })}
                                    label="Repost Percentage"
                                    endAdornment={<InputAdornment position="end">%</InputAdornment>}
                                    inputProps={{ min: 0, max: 100 }}
                                />
                            </FormControl>
                        </Box>
                        <Divider sx={{ my: 2 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                            <FormControlLabel 
                                control={<Checkbox checked={botsConfig?.reposterBot?.mixWithTimeline ?? false} onChange={(e) => handleChangeInternal({ ...botsConfig, reposterBot: { ...(botsConfig?.reposterBot || {}), mixWithTimeline: e.target.checked } })} />} 
                                label="Mix with Timeline Reposts" 
                            />
                        </Box>
                        {botsConfig?.reposterBot?.mixWithTimeline && (
                            <Box display="flex" flexWrap="wrap" gap={2}>
                                <FormControl sx={{ minWidth: 200, flex: 1, maxWidth: 300 }} variant="outlined">
                                    <InputLabel htmlFor="reposter-timeline-ratio">Timeline Posts Per Account Post</InputLabel>
                                    <OutlinedInput
                                        type='number'
                                        id='reposter-timeline-ratio'
                                        value={botsConfig?.reposterBot?.timelinePostsPerAccountPost ?? ''}
                                        onChange={(e) => handleChangeInternal({ ...botsConfig, reposterBot: { ...(botsConfig?.reposterBot || {}), timelinePostsPerAccountPost: parseInt(e.target.value, 10) || 5 } })}
                                        label="Timeline Posts Per Account Post"
                                        placeholder="5"
                                        inputProps={{ min: 1 }}
                                    />
                                    <Typography variant="caption" sx={{ mt: 0.5, color: 'text.secondary' }}>
                                        For every 1 post reposted from account list, repost this many from timeline
                                    </Typography>
                                </FormControl>
                                <FormControl sx={{ minWidth: 200, flex: 1, maxWidth: 300 }} variant="outlined">
                                    <InputLabel htmlFor="reposter-timeline-limit">Timeline Limit</InputLabel>
                                    <OutlinedInput
                                        type='number'
                                        id='reposter-timeline-limit'
                                        value={botsConfig?.reposterBot?.timelineLimit ?? ''}
                                        onChange={(e) => handleChangeInternal({ ...botsConfig, reposterBot: { ...(botsConfig?.reposterBot || {}), timelineLimit: parseInt(e.target.value, 10) || 50 } })}
                                        label="Timeline Limit"
                                        placeholder="50"
                                        inputProps={{ min: 1 }}
                                    />
                                    <Typography variant="caption" sx={{ mt: 0.5, color: 'text.secondary' }}>
                                        Maximum posts to check from timeline
                                    </Typography>
                                </FormControl>
                            </Box>
                        )}
                    </Box>
                </CardContent>
            </Card>

            <Box display="flex" gap={2} sx={{ mt: 3, justifyContent: 'center' }}>
                <Button type="button" variant="contained" onClick={handleSubmit} disabled={!changed} size="large">Save</Button>
                <Button 
                    type="button" 
                    variant="outlined" 
                    onClick={() => {
                        if (changed) {
                            setCancelDialogOpen(true);
                        } else {
                            handleCancel();
                        }
                    }}
                    size="large"
                >
                    Cancel
                </Button>
            </Box>

            <ConfirmDialog
                open={cancelDialogOpen}
                onClose={() => setCancelDialogOpen(false)}
                onConfirm={() => handleCancel()}
                title="Discard Changes?"
                message="You have unsaved changes. Are you sure you want to cancel? All changes will be lost."
                confirmText="Discard Changes"
                cancelText="Continue Editing"
                confirmColor="error"
                autoFocusConfirm={true}
            />
        </Box>
    );
};

export default BotsConfigEditForm;